import { ReactNode } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogProps,
  DialogTitle,
  IconButton,
  SxProps,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { FormattedMessage } from "react-intl";

import AppButton, { AppButtonIntent } from "./AppButton";
import { SX_FORM_CANCEL, SX_FORM_SUBMIT } from "../Configuration/styles";
import { slugify } from "../services/utils";
import useDialogUrl from "../hooks/useDialogUrl";

const TITLE_ID = "responsive-dialog-title";
const CONTENT_ID = "responsive-dialog-content";

interface ResponsiveDialogProps
  extends Pick<DialogProps, "open" | "fullWidth" | "maxWidth" | "slotProps"> {
  children?: ReactNode;
  // Omit for the default Cancel/Submit footer (driven by the `onSubmit`,
  // `formId`, `loading`… props below); pass `null` for a read-only dialog
  // (the cross is the only way out) or when the content renders its own
  // footer. Custom buttons take `SX_FORM_CANCEL` / `SX_FORM_SUBMIT` so they
  // line up with the default footer.
  actions?: ReactNode;
  contentText?: string;
  contentSx?: SxProps<Theme>;
  onClose?: () => void;
  // Omit for a title-less dialog (the cross then floats on the paper).
  title?: string;
  subtitle?: ReactNode;
  sx?: SxProps<Theme>;
  // Override the auto-generated URL slug (derived from title by default).
  // Set to false to disable URL sync for this dialog.
  dialogName?: string | false;
  // Entity identifier to include in the URL (?dialogId=<value>).
  dialogId?: string | number;
  // Default footer: the submit button either calls `onSubmit` or submits the
  // form whose id is `formId` (the content then owns the submit handler).
  onSubmit?: () => void;
  formId?: string;
  submitLabel?: ReactNode;
  submitIntent?: Extract<AppButtonIntent, "primary" | "danger">;
  loading?: boolean;
  submitDisabled?: boolean;
}

export default function ResponsiveDialog({
  actions,
  children,
  contentText,
  contentSx,
  fullWidth,
  onClose,
  maxWidth,
  open,
  title,
  subtitle,
  sx,
  dialogName,
  dialogId,
  slotProps,
  onSubmit,
  formId,
  submitLabel,
  submitIntent = "primary",
  loading,
  submitDisabled,
}: ResponsiveDialogProps) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const slug =
    dialogName === false || (dialogName === undefined && title === undefined)
      ? null
      : slugify(dialogName || title || "");
  const idStr = dialogId != null ? String(dialogId) : null;
  const { effectiveOpen, handleClose } = useDialogUrl(
    slug,
    !!open,
    onClose,
    idStr
  );

  const closeButton = (
    <IconButton
      aria-label="close"
      onClick={handleClose}
      sx={{ position: "absolute", right: 5, top: 5, zIndex: 1 }}
    >
      <CloseIcon />
    </IconButton>
  );

  return (
    <Dialog
      fullScreen={fullScreen}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      open={effectiveOpen}
      onClose={handleClose}
      aria-labelledby={title !== undefined ? TITLE_ID : undefined}
      aria-describedby={contentText ? CONTENT_ID : undefined}
      sx={sx}
      slotProps={slotProps}
    >
      {title !== undefined ? (
        <DialogTitle sx={{ textAlign: "center" }} id={TITLE_ID}>
          {title}
          {subtitle && (
            // A div, not a <p>: the subtitle may hold block content (chips).
            <Typography
              component="div"
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              {subtitle}
            </Typography>
          )}
          {closeButton}
        </DialogTitle>
      ) : (
        closeButton
      )}
      <DialogContent sx={contentSx}>
        {contentText && (
          <DialogContentText id={CONTENT_ID}>{contentText}</DialogContentText>
        )}
        {children}
      </DialogContent>
      {actions === undefined ? (
        // Actions padding (8px) + SX_FORM_* margins (24px) = the 32px inset of
        // the form fields (content padding + their own 8px margin): buttons
        // and fields share the same edges.
        <DialogActions>
          <AppButton
            intent="tonal"
            onClick={handleClose}
            disabled={loading}
            sx={SX_FORM_CANCEL}
          >
            <FormattedMessage id="CANCEL" />
          </AppButton>
          <AppButton
            intent={submitIntent}
            onClick={formId ? undefined : onSubmit}
            form={formId}
            type={formId ? "submit" : "button"}
            loading={loading}
            disabled={submitDisabled}
            sx={SX_FORM_SUBMIT}
          >
            <span>{submitLabel ?? <FormattedMessage id="SUBMIT" />}</span>
          </AppButton>
        </DialogActions>
      ) : (
        actions !== null && <DialogActions>{actions}</DialogActions>
      )}
    </Dialog>
  );
}
